import path from 'path'
import { format, Options } from 'prettier'

export const PRETTIER_CONFIG = {
  semi: true,
  singleQuote: false,
  tabWidth: 2,
  trailingComma: 'es5',
  printWidth: 80,
  arrowParens: 'always',
  // Disable certain features that might cause issues
  requirePragma: false,
  insertPragma: false,
  proseWrap: 'preserve',
} satisfies Partial<Options>

export const formatCode = async (code: string, filepath?: string): Promise<string> => {
  try {
    // Skip formatting if code is empty or too large
    if (!code || code.length > 1_000_000) {
      return code
    }

    // Try to format with Prettier
    return await format(code, {
      ...PRETTIER_CONFIG,
      filepath: filepath || 'file.ts',
    })
  } catch (err) {
    console.warn('Failed to format code with Prettier:', err)
    console.warn(
      code
        .slice(0, 1000)
        .split('\n')
        .map((l, i) => `\t${i.toString().padStart(2, '0')} | ${l}`)
        .join('\n')
    )
    return code
  }
}

export const ADK_VERSION = '1.0.0' // Update this to the current ADK version

export const relative = (from: string, to: string): string => {
  const fromDir = path.dirname(from)
  const relative = path.relative(fromDir, to)
  return relative.startsWith('.') ? relative : `./${relative}`
}

export function toMultilineComment(comment: string): string {
  // Handle empty or null comments
  if (!comment || comment.trim() === '') {
    return ''
  }

  // Escape potentially dangerous characters in the comment
  // Replace */ with *\/ to prevent premature comment closure
  let safeComment = comment.replace(/\*\//g, '*\\/')

  // Split by newlines to handle multi-line comments
  // Handle all line ending types: \r\n (Windows), \n (Unix), \r (old Mac)
  const lines = safeComment.split(/\r\n|\r|\n/)

  // If single line and short enough, return inline comment
  if (lines.length === 1 && lines[0] && lines[0].length <= 60) {
    return `/** ${lines[0]} */`
  }

  // For multi-line comments, format with proper indentation
  let result = '/**\n'
  for (const line of lines) {
    // Trim trailing whitespace but preserve leading whitespace for formatting
    const trimmedLine = line.replace(/\s+$/, '')
    result += ` * ${trimmedLine}\n`
  }
  result += ' */'

  return result
}
